import type { QueryFunction } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppState } from '../../AppStateProvider';
import type { Company, TopCountries } from '../../domain/company';
import { isOutOfQuota } from '../../domain/quota';

import { companyApi } from './api';
import companyQueryKey from './companyQueryKey';
import { updateTopCountriesQueryData } from './updateQueryData';
import { useCompanyQuery } from './useCompanyQuery';
import { useCompanyQuotaQuery } from './useCompanyQuotaQuery';

export const useTopCountriesQuery = () => {
  const queryClient = useQueryClient();
  const { isOpen, companyLinkedinId } = useAppState();
  const companyQuota = useCompanyQuotaQuery().data;
  const { domain: companyDomain = '' } = useCompanyQuery().data || {};

  const company = queryClient.getQueryData<Company>(
    companyQueryKey.company({ companyLinkedinId, domain: companyDomain })
  );

  const handleQuerySuccess = (topCountries?: TopCountries) => {
    if (!topCountries) {
      return;
    }

    if (company) {
      updateTopCountriesQueryData({ companyLinkedinId, company, topCountries, queryClient });
    }
  };

  const fetchTopCountries: QueryFunction<TopCountries, (string | undefined)[]> = async ({
    queryKey: [, , linkedinId, domain],
    signal,
  }) => {
    const data = await companyApi.getTopCountries({ linkedinId, domain }, signal);
    if (data) {
      handleQuerySuccess(data);
    }
    return data;
  };

  return useQuery({
    queryKey: companyQueryKey.topCountries({ companyLinkedinId, domain: companyDomain }),
    queryFn: fetchTopCountries,
    enabled: isOpen && !!company && (!!companyLinkedinId || !!companyDomain) && !isOutOfQuota(companyQuota),
    gcTime: Infinity,
  });
};
